// Constants for storage
const ONBOARDING_STORAGE_KEY = "onboardingData";

/**
 * Save onboarding data to local storage
 * @param {Object} data - The onboarding data to save
 */
export function saveOnboardingData(data) {
  if (typeof window === "undefined") return;

  try {
    // Process data to ensure it's safe to serialize and properly formatted
    const processedData = {
      ...data,
      // Add timestamp
      lastUpdated: new Date().toISOString(),
      
      // Ensure links are properly formatted
      links: Array.isArray(data.links) 
        ? data.links.map(link => {
            // If link is a string, convert to object
            if (typeof link === "string") {
              return { 
                url: link,
                metadata: {
                  url: link,
                  title: getTitleFromUrl(link)
                }
              };
            }
            
            // If link is object but has no metadata, add basic metadata
            if (typeof link === "object" && !link.metadata) {
              return {
                ...link,
                metadata: {
                  url: link.url,
                  title: getTitleFromUrl(link.url)
                }
              };
            }
            
            // Return link as is if it has metadata
            return link;
          })
        : data.links,
      
      // Ensure media items are properly formatted
      media: Array.isArray(data.media)
        ? data.media.map((item, index) => {
            // If media is a string, convert to object
            if (typeof item === "string") {
              return {
                id: `media-${index}-${Date.now()}`,
                type: item.includes("video") ? "video" : "image",
                url: item
              };
            }
            
            // Ensure media object has essential properties
            if (typeof item === "object") {
              return {
                id: item.id || `media-${index}-${Date.now()}`,
                type: item.type || "image",
                url: item.url || item.preview || "",
                // Keep only essential properties
                ...(item.contentType && { contentType: item.contentType }),
                ...(item.name && { name: item.name })
              };
            }
            
            return item;
          })
        : data.media,
    };

    // Save to localStorage
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(processedData));
    
    // Use sessionStorage as additional backup
    sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(processedData));
    
    console.log("Saved onboarding data:", {
      step: processedData.currentStep,
      tags: processedData.selectedTags?.length,
      links: processedData.links?.length,
      media: processedData.media?.length
    });
  } catch (e) {
    console.error("Error saving onboarding data:", e);
  }
}

/**
 * Extract a title from a URL
 */
function getTitleFromUrl(url) {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace("www.", "");
  } catch (e) {
    return url;
  }
}

/**
 * Get onboarding data from storage
 * @returns {Object|null} The saved onboarding data
 */
export function getOnboardingData() {
  if (typeof window === "undefined") return null;

  try {
    // Try localStorage first
    const localData = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (localData) {
      return JSON.parse(localData);
    }
    
    // Fall back to sessionStorage
    const sessionData = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (sessionData) {
      return JSON.parse(sessionData);
    }
  } catch (e) {
    console.error("Error retrieving onboarding data:", e);
  }
  
  return null;
}

/**
 * Clear onboarding data from storage
 */
export function clearOnboardingData() {
  if (typeof window === "undefined") return;

  try {
    // Clear from localStorage and sessionStorage
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
    
    // Also clear cookies
    document.cookie = `${ONBOARDING_STORAGE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `onboardingParams=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    
    console.log("Cleared all onboarding data");
  } catch (e) {
    console.error("Error clearing onboarding data:", e);
  }
}

/**
 * Determine the appropriate step based on saved data
 * @param {Object} data - The onboarding data
 * @returns {string} The appropriate step
 */
export function determineOnboardingStep(data) {
  if (!data) return "initial";

  // If the data has a current step, use it
  if (data.currentStep) return data.currentStep;

  // Otherwise determine based on what data is present
  const steps = [
    { check: d => !d.email, step: "initial" },
    { check: d => d.email && !d.password, step: "password" },
    { check: d => d.password && !d.slug, step: "slug" },
    { check: d => d.slug && (!d.selectedTags || d.selectedTags.length === 0), step: "tags" },
    { check: d => d.selectedTags?.length > 0 && (!d.links || !d.media || (d.links.length === 0 && d.media.length === 0)), step: "content" },
    { check: d => (d.links?.length > 0 || d.media?.length > 0) && !d.selectedTemplate, step: "template" },
    { check: d => d.selectedTemplate, step: "referral" }
  ];

  for (const { check, step } of steps) {
    if (check(data)) return step;
  }

  return "initial";
}
